import { useContext } from 'react'

import Bugsnag from '@bugsnag/js'
import { useTranslation } from 'react-i18next'

import { Country } from '@cash-web/protos/squareup/common/countries.pb'
import { GlobalAddress } from '@cash-web/protos/squareup/common/location.pb'
import { SetAddressResponseStatus } from '@cash-web/protos/squareup/franklin/app/address.pb'
import { AddressBlocker } from '@cash-web/protos/squareup/franklin/blockers/address_blocker.pb'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSetAddress } from '@cash-web/shared-data-access-api/react-query/useMutationSetAddress'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { useRegion } from '@cash-web/shared-region-ui-context-main'

import { catchPromise } from '../../../util/catchPromise'

const EXPECTED_REGIONS = ['GB', 'US', 'CA']

function regionDoesNotRequireState(region: Country) {
  return region === Country.GB
}

export const useAddress = ({ blocker }: { blocker: AddressBlocker }) => {
  const { t } = useTranslation()
  const { setError } = useContext(ErrorContext)
  const flow = useFlowContext()
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const { mutateAsync: setAddress } = useMutationSetAddress({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })
  const profileCountry = useAuth().profile?.country_code
  const regionCode = useRegion()
  const countryCode = profileCountry || regionCode
  if (countryCode && !EXPECTED_REGIONS.includes(countryCode)) {
    Bugsnag.notify({ name: 'BlockerAddress', message: `Unexpected region ${countryCode}` })
    throw new Error(
      `BlockerAddress expected a region that was one of ${EXPECTED_REGIONS.join(', ')} but got ${countryCode}.`
    )
  }

  async function onSubmit(postalAddress: GlobalAddress) {
    // Prevent submission if using autocomplete and no address fields are filled
    if (canUseAutocompleteAddress) {
      const hasAddressData =
        postalAddress.address_line_1 ||
        postalAddress.locality ||
        postalAddress.administrative_district_level_1 ||
        postalAddress.postal_code
      if (!hasAddressData) {
        setError(t('Please enter a mailing address', { context: 'Error text. empty address validation' }))
        return
      }
    }

    postalAddress.country_code = countryCode ? Country[countryCode] : undefined
    try {
      const response = await catchPromise(
        setAddress({
          postal_address: postalAddress,
          transfer_token: flow.active?.transferToken,
          payment_tokens: flow.active?.paymentTokens,
        })
      )

      const hasMessage = !!response.message
      const dialogMessage = response.response_context?.dialog_message
      const hasDialogMessage = !!dialogMessage
      const hasScenarioPlan = !!response.response_context?.scenario_plan
      const hasOnlyScenarioPlan = !hasMessage && !hasDialogMessage && hasScenarioPlan
      const isSuccess = response.status === SetAddressResponseStatus.SUCCESS
      const isFailure = response.status === SetAddressResponseStatus.FAILURE
      const isFailureWithScenarioPlan = isFailure && hasOnlyScenarioPlan

      if ((isSuccess || isFailureWithScenarioPlan) && !hasDialogMessage) {
        // Skipping advance to scenarioPlan if there is a message to render since the plan is just advancing to the current blocker
        // https://prototype.sqprod.co/#/docs/squareup.franklin.app.SetAddressResponse.Status#FAILURE
        await handleBlockerResponse({
          promisedBlockerUpdate: Promise.resolve(response),
        })
      } else {
        setError(
          dialogMessage ?? t('Sorry, there was an error. Try again?', { context: 'Error text. server error message' })
        )
      }
    } catch {
      setError(t('Sorry, there was an error. Try again?', { context: 'Error text. unknown error message' }))
      // TODO: send error information to logging platform
    }
  }

  const countryCodeTranslationKey = countryCode?.toLowerCase()

  const {
    requires_street_address: requiresStreetAddress,
    requires_city: requiresCity,
    requires_postal_code: requiresPostalCode,
  } = blocker
  // Sometimes the region suppresses the state requirement.
  const requiresState =
    (blocker.requires_state && countryCode && !regionDoesNotRequireState(Country[countryCode])) ?? false
  const postalCodeOnly = requiresPostalCode && !requiresStreetAddress && !requiresState && !requiresCity
  // Note: The `supplement?.main_text` is not being used so that the web
  // can have more control of the title and subtitle.
  let title = t('Enter your mailing address', { context: 'Title text. used for entering mailing address ' })
  let subtitle = null
  if (postalCodeOnly) {
    if (countryCodeTranslationKey === 'us') {
      title = t('Enter your ZIP code', { context: 'Title text.' })
      subtitle = t('Use your 5-digit ZIP code for your mailing address.', { context: 'Subtitle text.' })
    } else if (countryCodeTranslationKey === 'gb') {
      title = t('Enter your postcode', { context: 'Title text.' })
      subtitle = t('Use your postcode for your mailing address.', { context: 'Subtitle text.' })
    }
  }

  const doesRequireAllFields = requiresStreetAddress && requiresCity && requiresState && requiresPostalCode
  const canUseAutocompleteAddress =
    useFeatureFlag('cash-web-account-address-autocomplete') && countryCode === Country.US && doesRequireAllFields

  return { canUseAutocompleteAddress, title, subtitle, countryCode, requiresState, onSubmit }
}
